import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { Button } from '@components/ui';
import Accordion from '@components/ui/Accordion/Accordion';
import Modal from '@components/ui/Modal/Modal';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { getModules, getModuleMappings, syncModuleMappings } from '@/services/moduleService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';
import type { ModuleDto, ModuleMappingDto } from '@/types/module';
import styles from './ModuleRegistryTab.module.scss';

// ============================================
// TYPES
// ============================================

interface ModuleRegistryTabProps {
  organizationId: string;
}

// ============================================
// ICONS
// ============================================

function EditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.25 3H3C2.60218 3 2.22064 3.15804 1.93934 3.43934C1.65804 3.72064 1.5 4.10218 1.5 4.5V15C1.5 15.3978 1.65804 15.7794 1.93934 16.0607C2.22064 16.342 2.60218 16.5 3 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V9.75M13.875 1.875C14.1734 1.57663 14.578 1.40901 15 1.40901C15.422 1.40901 15.8266 1.57663 16.125 1.875C16.4234 2.17337 16.591 2.57805 16.591 3C16.591 3.42195 16.4234 3.82663 16.125 4.125L9 11.25L6 12L6.75 9L13.875 1.875Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function ModuleRegistryTab({ organizationId }: ModuleRegistryTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Tracks selected optional module IDs in edit mode
  const [editSelectedIds, setEditSelectedIds] = useState<Set<string>>(new Set());

  // Fetch tenant-visible modules (ALL + TENANT visibility)
  const { data: modulesData, isLoading: modulesLoading, error: modulesError } = useQuery({
    queryKey: ['modules', 'TENANT'],
    queryFn: () => getModules({ size: 100 }, { filters: { visibility: 'TENANT' } }),
  });

  // Fetch assigned module mappings for this tenant
  const { data: mappingsData, isLoading: mappingsLoading, error: mappingsError } = useQuery({
    queryKey: ['moduleMappings', organizationId],
    queryFn: () => getModuleMappings(organizationId, { size: 100 }),
    enabled: !!organizationId,
  });
  const isForbidden = isForbiddenError(modulesError) || isForbiddenError(mappingsError);

  const allModules = modulesData?.content ?? [];
  const mappings = mappingsData?.content ?? [];

  const assignedModuleMap = useMemo(() => {
    const map = new Map<string, ModuleMappingDto>();
    for (const m of mappings) {
      map.set(m.moduleId, m);
    }
    return map;
  }, [mappings]);

  const defaultModules = useMemo(
    () => allModules.filter((m: ModuleDto) => m.isDefault),
    [allModules],
  );

  const optionalModules = useMemo(
    () => allModules.filter((m: ModuleDto) => !m.isDefault),
    [allModules],
  );

  const isModuleAssigned = useCallback(
    (moduleId: string) => {
      const mapping = assignedModuleMap.get(moduleId);
      return mapping !== undefined && mapping.isEnabled;
    },
    [assignedModuleMap],
  );

  // --- View Modal ---
  const handleOpenEdit = useCallback(() => {
    // Initialize edit state from current assignments
    const selected = new Set<string>();
    for (const mod of optionalModules) {
      if (isModuleAssigned(mod.id)) {
        selected.add(mod.id);
      }
    }
    setEditSelectedIds(selected);
    setIsEditModalOpen(true);
  }, [optionalModules, isModuleAssigned]);

  const handleCloseEdit = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const toggleEditModule = useCallback((moduleId: string) => {
    setEditSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  // --- Save mutation ---
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // One server call replaces the previous "POST bulk adds + N DELETEs" diff:
      // backend reconciles to exactly this list (adds new, drops anything mapped
      // but absent). Defaults are unconditionally re-asserted so they survive a
      // sync even on the off chance a default isn't currently mapped.
      const desiredIds = [
        ...defaultModules.map((m) => m.id),
        ...Array.from(editSelectedIds),
      ];

      await syncModuleMappings(organizationId, desiredIds);

      void queryClient.invalidateQueries({ queryKey: ['moduleMappings', organizationId] });
      setIsEditModalOpen(false);
    } catch (error) {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    } finally {
      setSaving(false);
    }
  }, [editSelectedIds, defaultModules, organizationId, queryClient, addNotification, t]);

  // --- Loading ---
  const isLoading = modulesLoading || mappingsLoading;

  if (isLoading) {
    return <div className={styles.loading}>{t('K_LOADING_MODULES', 'Loading modules...')}</div>;
  }

  if (isForbidden) {
    return <Forbidden variant="inline" hideAction />;
  }

  if (allModules.length === 0) {
    return <div className={styles.emptyState}>{t('K_NO_MODULES_AVAILABLE', 'No modules available.')}</div>;
  }

  return (
    <div className={styles.container}>
      {/* Default Modules Accordion */}
      {defaultModules.length > 0 && (
        <Accordion
          title={t('K_DEFAULT_MODULES', 'Default Modules')}
          variant="filled"
          whiteBody
          defaultExpanded
        >
          <div className={styles.moduleList}>
            {defaultModules.map((mod) => (
              <label key={mod.id} className={styles.moduleItem}>
                <input
                  type="checkbox"
                  checked
                  disabled
                  className={styles.moduleCheckbox}
                />
                <div className={styles.moduleInfo}>
                  <span className={styles.moduleName}>{mod.name}</span>
                  {mod.description && (
                    <span className={styles.moduleDesc}>{mod.description}</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        </Accordion>
      )}

      {/* Optional Modules Accordion */}
      {optionalModules.length > 0 && (
        <Accordion
          title={t('K_OPTIONAL_MODULES', 'Optional Modules')}
          variant="filled"
          whiteBody
          defaultExpanded
          action={
            hasPermission('MODULE_REGISTRY_MANAGE') ? (
              <button
                type="button"
                className={styles.editButton}
                onClick={handleOpenEdit}
                aria-label="Edit optional modules"
              >
                <EditIcon />
              </button>
            ) : undefined
          }
        >
          <div className={styles.moduleList}>
            {optionalModules.map((mod) => {
              const assigned = isModuleAssigned(mod.id);
              return (
                <label key={mod.id} className={styles.moduleItem}>
                  <input
                    type="checkbox"
                    checked={assigned}
                    disabled
                    className={styles.moduleCheckbox}
                  />
                  <div className={styles.moduleInfo}>
                    <span className={styles.moduleName}>{mod.name}</span>
                    {mod.description && (
                      <span className={styles.moduleDesc}>{mod.description}</span>
                    )}
                  </div>
                  {assigned && <span className={styles.assignedBadge}>{t('K_BADGE_ASSIGNED', 'Assigned')}</span>}
                </label>
              );
            })}
          </div>
        </Accordion>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEdit}
        title={t('K_MODAL_EDIT_MODULE_REGISTRY', 'Edit Module Registry')}
        size="md"
        footer={
          <div className={styles.modalFooter}>
            <Button variant="outline" size="sm" onClick={handleCloseEdit} disabled={saving}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          </div>
        }
      >
        <div className={styles.editContent}>
          {/* Optional — toggleable */}
          {optionalModules.length > 0 && (
            <div className={styles.editSection}>
              <div className={styles.moduleList}>
                {optionalModules.map((mod) => (
                  <label key={mod.id} className={styles.moduleItem}>
                    <input
                      type="checkbox"
                      checked={editSelectedIds.has(mod.id)}
                      onChange={() => toggleEditModule(mod.id)}
                      className={styles.moduleCheckbox}
                    />
                    <div className={styles.moduleInfo}>
                      <span className={styles.moduleName}>{mod.name}</span>
                      {mod.description && (
                        <span className={styles.moduleDesc}>{mod.description}</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
}

export default ModuleRegistryTab;
